import React, { Component } from 'react';
import styled from '@emotion/styled';

import { convertToRaw, EditorState, Modifier } from 'draft-js';
import Editor, { PluginEditorProps } from '@draft-js-plugins/editor';
import createMentionPlugin, {
  MentionData,
  MentionPluginConfig,
  MentionPluginTheme,
} from '@draft-js-plugins/mention';
import { debounce } from 'lodash';

import { SPACING } from 'Style/spacing';
import {
  TEXT_COLORS,
  SURFACE_COLORS,
  DIVIDER_COLORS,
  COLORS,
} from 'Style/colors';

import ProfileAvatar from 'Webapp/shared/app/components/profile-avatar.js';
import AtMention from 'Webapp/shared/app/components/at-mention';
import connector from 'Webapp/utils/connector';
import connectMentionables, {
  ConnectMentionablesProps,
} from 'Webapp/shared/app/connectors/connectMentionables';
import connectFeatureFlags, {
  ConnectFeatureFlagsProps,
} from 'Webapp/shared/app/connectors/connectFeatureFlags';
import { BREAKPOINTS } from 'Style/breakpoints';
import { LAYERS } from 'Style/layout';
import { noOpFn } from 'Webapp/utils/no-op-fn';
import { MentionableSuggestion } from 'Webapp/shared/concepts/mentionables';

export const MENTION_AVATAR_SIZE = 20;
export interface DesktopCommentInputPassedProps {
  className?: string;
  onChange: ({
    value,
    mentionLinks,
  }: {
    value: string;
    mentionLinks?: Array<Flipboard.MentionLink>;
  }) => void;
  onFocus: () => void;
  focusInput: boolean;
  keyBindingFn?: () => void;
  // TODO convert withT to ts and export props
  t: Flipboard.TFunction;
  // TODO convert withToast to ts and export props
  toastGenericError: () => void;
  triggerMention: boolean;
  beginFocused: boolean;
  searchMagazineId: Flipboard.SectionId;
}

type DesktopCommentInputProps = DesktopCommentInputPassedProps &
  ConnectMentionablesProps &
  ConnectFeatureFlagsProps &
  Pick<
    PluginEditorProps,
    'readOnly' | 'placeholder' | 'keyBindingFn' | 'handleKeyCommand'
  >;

interface DesktopCommentInputState {
  editorState: EditorState;
  suggestions: Array<MentionData>;
  mentionsOpen: boolean;
  mentionSearchValue: string;
}

const CommentMention: MentionPluginConfig['mentionComponent'] = ({
  children,
  className,
  mention: { avatar, name, username },
}) => (
  <AtMention
    className={className}
    avatar={avatar}
    name={name}
    username={username}
  >
    {/* This is auto sent to us by draftJS as the "name"
     in the mention prop.
     Weird things happen if you change this. */}
    {children}
  </AtMention>
);

const MentionSuggestionUsername = styled.span({
  color: TEXT_COLORS.tertiary,
});

const MentionSuggestionWrapper = styled.div({
  padding: SPACING.SMALL,
  display: 'flex',
  gap: SPACING.BASE,
  alignItems: 'center',
  cursor: 'pointer',
  '&[aria-selected=true]': {
    background: SURFACE_COLORS.secondary,
    '.mention-suggestion-inner': {
      background: SURFACE_COLORS.primary,
    },
  },
});

const Wrapper = styled.div({
  zIndex: LAYERS.DROPDOWN,
  cursor: 'text',
  minWidth:
    '1px' /* hack: the wrapper needs a min width to avoid overflow issue: https://flipboard.atlassian.net/browse/FL-29282 */,
  '.mentionSuggestions': {
    zIndex: LAYERS.BASE,
    background: SURFACE_COLORS.primary,
    border: `1px solid ${DIVIDER_COLORS.primary}`,
    borderRadius: SPACING.SMALL,
    boxShadow: '0 1px 2px rgba(0,0,0,0.3)',
  },
  '.public-DraftEditorPlaceholder-root': {
    paddingLeft: SPACING.MEDIUM,
    position: 'absolute',
    color: TEXT_COLORS.tertiary,
    overflowY: 'auto',
    display: 'grid',
    height: '100%',
  },
  '.DraftEditor-root': {
    position: 'relative',
    display: 'grid',
    height: '100%',
    width: '100%',
    marginTop: SPACING.MEDIUM,
    '.mention': {
      margin: `0 ${SPACING.XSMALL}`,
    },
  },
  '.public-DraftStyleDefault-block': {
    padding: `0 ${SPACING.MEDIUM}`,
    width: '100%',
    lineHeight: '1.6', // Bumping the line height to make the mentions look a little better.
    caretColor: COLORS.red,
  },
  '.DraftEditor-editorContainer': {
    display: 'grid',
    height: '100%',
  },
  '.public-DraftEditor-content': {
    maxHeight: '10vh',
    overflow: 'auto',
    ...BREAKPOINTS.tabletPortraitUp({
      maxHeight: '30vh',
    }),
    span: { minWidth: '1px' }, // Stupid hack to make sure there is always a cursor visible in Chrome.
  },
});

// TODO should be able to get this from draft-js/mention types but
// can't figure out how
type MentionSuggestionProps = {
  mention: MentionData;
  theme?: MentionPluginTheme;
  className?: string;
} & ConnectFeatureFlagsProps;

const MentionSuggestion: React.FC<MentionSuggestionProps> = ({
  mention: { avatar, fullName, username },
  className,
  featureFlags,
  ...props
}) => (
  <MentionSuggestionWrapper className={className} {...props}>
    {featureFlags.PILL_REMOVAL ? (
      <>
        <ProfileAvatar
          name={fullName}
          avatarSize={MENTION_AVATAR_SIZE}
          image={avatar}
        />
        <span>{fullName}</span>
        <AtMention
          className="mention-suggestion-inner"
          name={fullName}
          avatar={avatar}
          username={username}
        >
          {username}
        </AtMention>
      </>
    ) : (
      <>
        <AtMention
          className="mention-suggestion-inner"
          name={fullName}
          avatar={avatar}
          username={username}
        >
          {fullName}
        </AtMention>
        <MentionSuggestionUsername>@{username}</MentionSuggestionUsername>
      </>
    )}
  </MentionSuggestionWrapper>
);
const ConnectedMentionSuggestion = connector<
  { mention: MentionData } & ConnectFeatureFlagsProps
>(connectFeatureFlags)(MentionSuggestion);

class DesktopCommentInput extends Component<
  DesktopCommentInputProps,
  DesktopCommentInputState
> {
  state: DesktopCommentInputState = {
    editorState: EditorState.createEmpty(),
    suggestions: [],
    mentionsOpen: false,
    mentionSearchValue: '',
  };

  componentDidMount() {
    if (this.props.beginFocused) {
      // give draftjs a tick to be ready to focus
      setTimeout(() => this.focus());
    }
  }

  componentDidUpdate(prevProps: DesktopCommentInputProps) {
    if (!prevProps.focusInput && this.props.focusInput) {
      this.focus();
    }
    if (!prevProps.triggerMention && this.props.triggerMention) {
      this.triggerMention();
    }
  }

  triggerMention = () => {
    const contentState = this.state.editorState.getCurrentContent();
    const selectionState = this.state.editorState.getSelection();

    // if we don't have a space where we are inserting we need to add
    // one
    const anchorKey = selectionState.getAnchorKey();
    const currentContentBlock = contentState.getBlockForKey(anchorKey);
    const selectionStart = selectionState.getStartOffset();
    const selectionEnd = selectionState.getEndOffset();
    const previousCharacter = currentContentBlock
      .getText()
      .slice(selectionStart - 1, selectionStart);
    const insertText = previousCharacter === ' ' ? '@' : ' @';

    const selectionExists = selectionStart !== selectionEnd;

    const newContentState = selectionExists
      ? Modifier.replaceText(contentState, selectionState, insertText)
      : Modifier.insertText(contentState, selectionState, insertText);
    const editorState = EditorState.push(
      this.state.editorState,
      newContentState,
      'insert-characters',
    );
    this.setState({ editorState }, () => {
      this.focus();
    });
  };

  searchMentionTimeout: number | null = null;
  searchMentionablesController: AbortController | undefined;
  editor = React.createRef<Editor>();
  mentionPlugin = createMentionPlugin({
    mentionComponent: this.props.featureFlags?.PILL_REMOVAL
      ? undefined
      : CommentMention,
    entityMutability: 'IMMUTABLE',
    mentionPrefix: this.props.featureFlags?.PILL_REMOVAL ? '@' : '',
    supportWhitespace: true,
    theme: {
      mention: 'mention',
      mentionSuggestions: 'mentionSuggestions',
    },
    popperOptions: {
      placement: 'auto-start',
      modifiers: [
        {
          name: 'flip',
          options: {
            allowedAutoPlacements: ['top-start', 'bottom-start'], // by default, all the placements are allowed
          },
        },
        {
          name: 'offset',
          options: {
            offset: ({ placement, reference }) => {
              // reference.height should refer to the trigger of the mention (the '@' symbol in our case)
              if (placement.startsWith('bottom')) {
                return [0, 0];
              } else if (placement.startsWith('top')) {
                return [0 - reference.height + 16, 0];
              }
              return [];
            },
          },
        },
      ],
    },
  });

  onInputChange = (editorState: EditorState) => {
    this.setState({
      editorState,
    });
    const mentionLinks: Array<Flipboard.MentionLink> = [];
    const value = editorState.getCurrentContent().getPlainText();

    const cleanedValue = (value || '').trimStart();
    try {
      const { blocks, entityMap } = convertToRaw(
        editorState.getCurrentContent(),
      );

      // we trimmed, so we must shift the offset
      let offset = cleanedValue.length - value.length;
      blocks.forEach((block, blockIndex) => {
        block.entityRanges.forEach((entityRange) => {
          mentionLinks.push({
            textLoc: [
              // offset of previous block text + block newline + offset of mention in block
              offset + blockIndex + entityRange.offset,
              entityRange.length,
            ],
            type: 'user',
            id: entityMap[entityRange.key].data.mention.id,
          });
        });
        offset = offset + block.text.length;
      });
    } catch (_) {
      // nuthin
    }
    this.props.onChange({ value: cleanedValue, mentionLinks });
  };

  mentionsOpenChange = (mentionsOpen: boolean) =>
    this.setState({ mentionsOpen });

  searchMentions = async ({ value }: { value: string }) => {
    // the user put a space after the @ or is "spacing away" from the
    // value they started typing
    if (value === ' ' || value?.match(/\s{2,}$/)) {
      this.setState({ suggestions: [], mentionsOpen: false });
      return;
    }
    const { fetchZeroStateMentionables, searchMentionables, searchMagazineId } =
      this.props;
    let suggestions: Array<MentionableSuggestion> = [];

    try {
      if (value) {
        suggestions = await searchMentionables(value, searchMagazineId);
      } else {
        suggestions = await fetchZeroStateMentionables();
      }
    } catch {
      // no-op
    }
    this.setState({
      suggestions,
    });
  };

  onSearchChange = debounce(this.searchMentions, 200);

  focus = () => {
    this.editor.current?.focus();
  };

  render() {
    const { mentionsOpen, suggestions, editorState } = this.state;
    const {
      className,
      placeholder,
      readOnly,
      keyBindingFn,
      handleKeyCommand,
      onFocus,
    } = this.props;
    const { MentionSuggestions } = this.mentionPlugin;
    const plugins = [this.mentionPlugin];

    return (
      <Wrapper className={className} onClick={this.focus}>
        <MentionSuggestions
          entryComponent={ConnectedMentionSuggestion}
          open={mentionsOpen}
          onSearchChange={this.onSearchChange}
          suggestions={suggestions}
          onOpenChange={this.mentionsOpenChange}
          onAddMention={() => this.setState({ suggestions: [] })}
        />
        <Editor
          editorKey="comment"
          readOnly={readOnly}
          editorState={editorState}
          onChange={this.onInputChange}
          onFocus={onFocus || noOpFn}
          plugins={plugins}
          ref={this.editor}
          placeholder={placeholder}
          handleKeyCommand={!mentionsOpen ? handleKeyCommand : undefined}
          keyBindingFn={!mentionsOpen ? keyBindingFn : undefined}
        />
      </Wrapper>
    );
  }
}

export default connector<DesktopCommentInputProps>(
  connectMentionables,
  connectFeatureFlags,
)(DesktopCommentInput);
